<template>
  <div class="coupon-dashboard">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">
          <v-icon class="title-icon">mdi-trophy-award</v-icon>
          Redeem History
        </h1>
        <p class="page-subtitle">Track and manage your earned rewards</p>
      </div>
    </div>
           <!-- Show error message below the redeem history box -->
    <div v-if="errorMessage"  style="text-align:center;">
      {{ errorMessage }}
    </div>


 

    <div class="coupons-grid">
      <div
        v-for="coupon in reedemedCoupons"
        :key="coupon._id"
        class="coupon-ticket"
        :class="{
          'active-coupon': !isExpired(coupon.expiryDate),
          'expired-coupon': isExpired(coupon.expiryDate)
        }"
      >
      
        <div class="coupon-header">
          <div class="coupon-badge">
            <v-icon color="white">mdi-ticket-confirmation</v-icon>
          </div>
          <h3 class="coupon-name">{{ coupon.title }}</h3>
          <div class="coupon-status">
            <v-chip
              small
              :color="isExpired(coupon.expiryDate) ? 'error' : 'success'"
              dark
            >
              {{ isExpired(coupon.expiryDate) ? "Expired" : "Active" }}
            </v-chip>
          </div>
        </div>

        <div class="coupon-body">
          <div class="coupon-details">
            <div class="detail-item">
              <v-icon small>mdi-clock-outline</v-icon>
              <span>Valid until {{ formatDate(coupon.expiryDate) }}</span>
            </div>
            <div class="detail-item">
              <v-icon small>mdi-target</v-icon>
              <span>Earned after {{ coupon.goal_hours }} volunteer hours</span>
            </div>
          </div>

          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="terms-toggle">
                <template>
                  <span>Terms & Conditions</span>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="terms-content">{{ coupon.description }}</div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div
            v-if="
              !isExpired(coupon.expiryDate) &&
                profile.type === 'Volunteer' &&
                coupon.reedemedUsers.some(
                  user => user.redeemed_by === profile._id
                )
            "
            class="coupon-code-section"
          >
            <div
              class="code-display"
              @click="copyCouponCode(coupon.couponCode)"
            >
              <div class="code-label">Your coupon code:</div>
              <div class="code-value">
                {{ coupon.couponCode }}
                <v-icon small class="copy-icon">mdi-content-copy</v-icon>
              </div>
              <div class="code-hint">Click to copy</div>
            </div>
          </div>
        </div>

        <div class="coupon-perforation"></div>

        <div class="coupon-footer">
          <div class="coupon-actions">
            <!-- <v-btn 
              v-if="!isExpired(coupon.expiryDate)"
              small 
              outlined 
              color="primary"
              @click="shareCoupon(coupon)"
            >
              <v-icon small left>mdi-share-variant</v-icon>
              Share
            </v-btn> -->
            <v-btn small text color="secondary" @click="viewHowToUse(coupon)">
              <v-icon small left>mdi-help-circle</v-icon>
              How to use
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-snackbar v-model="snackbar" :color="snackColor" top right>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="howToUseDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">How to Use Your Coupon</v-card-title>
        <v-card-text>
          <div class="usage-steps">
            <div class="step">
              <div class="step-number">1</div>
              <div class="step-content">Copy the coupon code above</div>
            </div>
            <div class="step">
              <div class="step-number">2</div>
              <div class="step-content">
                Visit the partner website or location
              </div>
            </div>
            <div class="step">
              <div class="step-number">3</div>
              <div class="step-content">
                Apply the code at checkout or present it to staff
              </div>
            </div>
            <div class="step">
              <div class="step-number">4</div>
              <div class="step-content">Enjoy your reward!</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="howToUseDialog = false"
            >Got it!</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import dateFormat from "dateformat";

export default {
  data() {
    return {
      snackbar: false,
      snackMessage: null,
      snackColor: "success",
      reedemedCoupons: [],
      howToUseDialog: false,
      currentCoupon: null,
      errorMessage: null
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    })
  },
  methods: {
    ...mapActions("awards", {
      redeemedAwards: "fetchRedeemedAwards"
    }),

    async copyCouponCode(code) {
      try {
        await navigator.clipboard.writeText(code);
        this.snackMessage = "Coupon code copied to clipboard!";
        this.snackColor = "success";
        this.snackbar = true;
      } catch (err) {
        console.error("Failed to copy: ", err);
        this.snackMessage = "Failed to copy coupon code!";
        this.snackColor = "error";
        this.snackbar = true;
      }
    },

    redeemedCoupons() {
      this.redeemedAwards({})
        .then(r => {
          this.reedemedCoupons = r;
        })
        .catch(error => {
          console.log(error);
          this.errorMessage = error;
        });
    },

    formatDate(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "mmm d, yyyy");
    },

    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    },

    isExpired(expiryDate) {
      if (!expiryDate) return false;
      const today = new Date();
      const expiry = new Date(expiryDate);
      return expiry <= today;
    },

    shareCoupon(coupon) {
      if (navigator.share) {
        navigator
          .share({
            title: `My Reward: ${coupon.title}`,
            text: `Use this coupon code: ${coupon.couponCode}`,
            url: window.location.href
          })
          .catch(err => {
            console.log("Error sharing:", err);
            this.copyCouponCode(coupon.couponCode);
          });
      } else {
        this.copyCouponCode(coupon.couponCode);
      }
    },

    viewHowToUse(coupon) {
      this.currentCoupon = coupon;
      this.howToUseDialog = true;
    }
  },
  mounted() {
    this.redeemedCoupons();
  }
};
</script>

<style scoped>
.coupon-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  /* background: linear-gradient(135deg, #0a5b8a, #1da1f2) !important; */
  border-radius: 16px;
}

.page-title {
  font-family: "Poppins-SemiBold";
  color: #333;
  text-align: center;
  margin-bottom: 32px;
  font-size: 28px;
}

.coupons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.coupon-ticket {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  background: white;
  border-left: 6px solid #4caf50;
}

.active-coupon {
  border-left-color: #4caf50;
}

.expired-coupon {
  border-left-color: #f44336;
  opacity: 0.8;
}

.coupon-header {
  padding: 16px;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  border-bottom: 1px dashed #ddd;
}

.coupon-badge {
  background-color: #4caf50;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.expired-coupon .coupon-badge {
  background-color: #f44336;
}

.coupon-name {
  font-family: "Poppins-SemiBold";
  font-size: 18px;
  color: #333;
  margin: 0;
  flex-grow: 1;
}

.coupon-status {
  margin-left: 12px;
}

.coupon-body {
  padding: 16px;
}

.coupon-details {
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
}

.detail-item i {
  margin-right: 8px;
  color: #0a5b8a;
}

.terms-toggle {
  padding: 0;
  min-height: 40px;
  font-size: 14px;
  color: #0a5b8a;
  font-family: "Poppins-SemiBold";
}

.terms-content {
  padding: 8px 0;
  font-size: 14px;
  color: #666;
  white-space: pre-line;
}

.coupon-code-section {
  margin-top: 16px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.code-display {
  cursor: pointer;
  transition: all 0.2s;
}

.code-display:hover {
  transform: scale(1.02);
}

.code-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.code-value {
  font-family: "Poppins-SemiBold";
  font-size: 20px;
  letter-spacing: 2px;
  color: #2e7d32;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  border: 1px dashed #4caf50;
}

.expired-coupon .code-value {
  color: #757575;
  border-color: #bdbdbd;
}

.copy-icon {
  margin-left: 8px;
  color: #0a5b8a;
}

.code-hint {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.coupon-perforation {
  height: 4px;
  background: repeating-linear-gradient(
    to right,
    #f5f5f5,
    #f5f5f5 10px,
    #ddd 10px,
    #ddd 20px
  );
  margin: 0 16px;
}

.coupon-footer {
  padding: 12px 16px;
  background-color: #f8f9fa;
}

.coupon-actions {
  display: flex;
  justify-content: space-between;
}

.usage-steps {
  padding: 8px 0;
}

.step {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.step-number {
  width: 28px;
  height: 28px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: bold;
}

.step-content {
  font-size: 15px;
  color: #333;
}

@media (max-width: 768px) {
  .coupons-grid {
    grid-template-columns: 1fr;
  }

  .coupon-ticket {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .coupon-dashboard {
    padding: 16px;
  }

  .page-title {
    font-size: 24px;
    margin-bottom: 24px;
  }

  .coupon-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .coupon-badge {
    margin-bottom: 8px;
  }

  .coupon-status {
    margin-left: 0;
    margin-top: 8px;
  }

  .coupon-actions {
    flex-direction: column;
    gap: 8px;
  }

  .coupon-actions .v-btn {
    width: 100%;
  }
}

.header-section {
  text-align: center;
  margin-bottom: 20px;
  /* padding: 40px 20px; */
}

.header-content {
  max-width: 600px;
  margin: 0 auto;
}

.page-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.title-icon {
  font-size: 3rem !important;
  color: #ffd700;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin: 0;
}
</style>
